/**
 * @fileoverview Reusable Checklist Question Component
 * @module ChecklistQuestion
 * 
 * Supports optional follow-up fields that appear when "Yes" is selected
 * for questions that need additional details (e.g., professional_opinion).
 * 
 * Includes "Learn more" trigger that opens the Helper Blade with detailed
 * guidance for each checklist item.
 * 
 * Supports inline photo uploads for photo-worthy items, allowing users to
 * upload photos directly when marking an issue.
 */

import React from 'react';
import { Check, X } from 'lucide-react';
import FollowUpFields, { FollowUpConfig } from '../common/FollowUpFields';
import { MAINTENANCE_FOLLOW_UP_CONFIGS } from '../common/followUpConfigs';
import { hasHelperContent } from './helper-content';
import { isPhotoWorthyItem, getPhotoConfig } from './photo-worthy-items';
import InlinePhotoUpload from '../evidence/InlinePhotoUpload';

// Shared evidence document interface - re-exported for use by checklist components
export interface EvidenceDocument {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  checklistItemId?: string | null;
  uploadedAt: string;
}

interface ChecklistQuestionProps {
  id: string;
  question: string;
  helpText?: string | undefined;
  value: boolean | null;
  onChange: (value: boolean) => void;
  // Follow-up data support
  followUpData?: Record<string, string | number> | undefined;
  onFollowUpChange?: ((fieldName: string, value: string | number) => void) | undefined;
  // Helper blade trigger
  onHelpClick?: ((itemId: string) => void) | undefined;
  // Photo upload support (optional - requires findingId to be enabled)
  findingId?: string | undefined;
  itemPhotos?: EvidenceDocument[] | undefined;
  onPhotoUpload?: ((evidence: EvidenceDocument) => void) | undefined;
  onPhotoDelete?: ((evidenceId: string) => void) | undefined;
}

const ChecklistQuestion: React.FC<ChecklistQuestionProps> = ({
  id,
  question,
  helpText,
  value,
  onChange,
  followUpData = {},
  onFollowUpChange,
  onHelpClick,
  findingId,
  itemPhotos = [],
  onPhotoUpload,
  onPhotoDelete
}) => {
  // Check if this question has follow-up fields
  const followUpConfig: FollowUpConfig | undefined = MAINTENANCE_FOLLOW_UP_CONFIGS[id];
  const hasFollowUp = !!followUpConfig;
  
  // Check if this question has extended helper content
  const hasHelper = hasHelperContent(id);

  // Check if this is a photo-worthy item (only show if we have a findingId and callbacks)
  const photoConfig = getPhotoConfig(id);
  const showPhotoUpload = value === true && findingId && photoConfig && onPhotoUpload;

  return (
    <div className="py-4 border-b border-gray-100 last:border-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <label htmlFor={id} className="text-gray-800 font-medium cursor-pointer">
            {question}
          </label>
          {/* Help text */}
          {helpText && (
            <p className="mt-1 text-sm text-gray-500">
              {helpText}
            </p>
          )}
          {/* Learn more on its own line */}
          {hasHelper && onHelpClick && (
            <button
              type="button"
              onClick={() => onHelpClick(id)}
              className="mt-1 text-sm text-teal-600 hover:text-teal-700 font-medium hover:underline transition-colors"
              title="Learn more about this item"
            >
              Learn more
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(true)}
            className={`p-2 rounded-lg border-2 transition-all ${
              value === true
                ? 'bg-green-50 border-green-500 text-green-600'
                : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-green-300 hover:text-green-500'
            }`}
            title="Yes, this is an issue"
          >
            <Check className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => onChange(false)}
            className={`p-2 rounded-lg border-2 transition-all ${
              value === false
                ? 'bg-red-50 border-red-500 text-red-600'
                : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-500'
            }`}
            title="No issue"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Follow-up fields for questions that need additional details */}
      {hasFollowUp && onFollowUpChange && (
        <FollowUpFields
          config={followUpConfig}
          values={followUpData}
          onChange={onFollowUpChange}
          isVisible={value === true}
        />
      )}

      {/* Inline photo upload for photo-worthy items */}
      {showPhotoUpload && (
        <InlinePhotoUpload
          findingId={findingId}
          checklistItemId={id}
          promptText={photoConfig.promptText}
          guidanceTips={photoConfig.guidanceTips}
          existingPhotos={itemPhotos}
          onUploadComplete={onPhotoUpload}
          onDeletePhoto={onPhotoDelete}
        />
      )}
    </div>
  );
};

export default ChecklistQuestion;








